/**
 * Printable quality investigation traveler. Preview is the duplex front/back
 * sheet. Print sends that PDF to the Case Management CUPS printer.
 */

import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { PageTitleWithHelp } from "@/components/ui/title-help";
import { fetchQualityCaseTravelerHtml, qualityCaseLabel } from "./casesApi";
import { QualityInvestigationTravelerPrintControls } from "./QualityInvestigationTravelerPrintControls";

export function QualityInvestigationTravelerPage(): JSX.Element {
  const params = useParams<{ id: string; investigationId?: string }>();
  const caseId = Number(params.id);
  const investigationId = params.investigationId ? Number(params.investigationId) : null;
  const requestedInvestigationId =
    investigationId != null && Number.isFinite(investigationId) && investigationId > 0
      ? investigationId
      : null;

  const htmlQuery = useQuery({
    queryKey: ["quality", "case", caseId, "traveler-html", requestedInvestigationId],
    queryFn: () => fetchQualityCaseTravelerHtml(caseId, requestedInvestigationId),
    enabled: Number.isFinite(caseId) && caseId > 0,
    refetchOnWindowFocus: false,
  });

  if (!Number.isFinite(caseId) || caseId < 1) {
    return <p className="text-sm text-red-700">Invalid quality case id.</p>;
  }

  if (htmlQuery.isLoading) {
    return <p className="text-sm text-slate-600">Loading investigation traveler…</p>;
  }

  if (htmlQuery.isError) {
    return <p className="text-sm text-red-700">{(htmlQuery.error as Error).message}</p>;
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3 print:hidden">
        <PageTitleWithHelp
          hoverHint="One letter sheet per linked unit, front for custody and back for findings. Print sends the duplex PDF to the Case Management printer."
          breadcrumbLabel="Investigation traveler"
        >
          {qualityCaseLabel(caseId)} traveler
        </PageTitleWithHelp>
        <QualityInvestigationTravelerPrintControls
          caseId={caseId}
          investigationId={requestedInvestigationId}
        />
      </header>
      <iframe
        title={`${qualityCaseLabel(caseId)} traveler preview`}
        className="h-[11in] w-full rounded-md border border-slate-200 bg-white print:h-auto print:border-0"
        srcDoc={htmlQuery.data ?? ""}
      />
    </div>
  );
}
